import React, { useState } from "react";
import { Download } from "lucide-react";
import { toPng } from "html-to-image";
import { alertDialog } from "../utils/dialogs";

/**
 * Small button that downloads the closest .printable container as PNG.
 * Pass either a ref OR a getter function returning the DOM node.
 *
 * The PNG is rendered with a WHITE background so coaches don't burn ink
 * printing the dark dashboard chrome. We temporarily attach
 * `data-print-light` to the captured node + `<body>` so a print-only CSS
 * cascade can recolour text / grid lines / cards for legibility on white.
 *
 * Props:
 *   targetRef: React ref to an element
 *   getNode:   () => HTMLElement (alternative to targetRef)
 *   filename:  string (default 'chart.png')
 *   label:     button text (default 'Print')
 */
export default function PrintButton({ targetRef, getNode, filename = "chart.png", label = "Print" }) {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    const node = getNode ? getNode() : targetRef?.current;
    if (!node) return;
    setBusy(true);
    // Toggle the "ink-friendly" theme on the target subtree just for the
    // capture. The matching CSS lives in `index.css` under `[data-print-light]`.
    node.setAttribute("data-print-light", "true");
    document.body.setAttribute("data-print-light", "true");
    try {
      // Browsers occasionally need a frame for layout to settle after the
      // attribute is set (recharts text re-flows when colour shifts).
      await new Promise((res) => requestAnimationFrame(res));
      const dataUrl = await toPng(node, {
        backgroundColor: "#FFFFFF",
        pixelRatio: 2,
        cacheBust: true,
        // Skip web-font embedding (Google Fonts blocks cross-origin cssRules);
        // chart text will use the system fallback in the exported PNG.
        fontEmbedCSS: "",
        // skip elements that opt out of print
        filter: (el) => !(el?.dataset && el.dataset.noprint === "true"),
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      a.click();
    } catch (e) {
      console.error("Print failed:", e);
      alertDialog("Sorry, could not export this chart.");
    } finally {
      node.removeAttribute("data-print-light");
      document.body.removeAttribute("data-print-light");
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className="pill !text-[11px] !py-1 !px-2.5"
      data-no-print="true"
      data-testid="print-btn"
      title="Download chart as PNG"
    >
      <Download size={12} />
      {busy ? "..." : label}
    </button>
  );
}
